import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import {
  employeesDisableById,
  employeesEnableById,
  employeesGetById,
} from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/employees/$id/toggle-status"
)({
  component: ToggleEmployeeStatusModal,
});

function ToggleEmployeeStatusModal() {
  const { id: employeeId } = Route.useParams();
  const navigate = useNavigate();

  const { data: employee, isPending } = useQuery(
    convexQuery(employeesGetById, {
      id: toConvexTableId("employees", employeeId),
    })
  );

  const disableEmployeeMutation = useMutation({
    mutationFn: ({ id }: { id: Id<"employees"> }) => {
      return convexClient.mutation(employeesDisableById, { id });
    },
    onSuccess: (_result, variables) => {
      // Track employee status update in PostHog
      ProductEvents.employeeUpdated(variables.id);
    },
  });

  const enableEmployeeMutation = useMutation({
    mutationFn: ({ id }: { id: Id<"employees"> }) => {
      return convexClient.mutation(employeesEnableById, { id });
    },
    onSuccess: (_result, variables) => {
      // Track employee status update in PostHog
      ProductEvents.employeeUpdated(variables.id);
    },
  });

  const handleClose = () => {
    void navigate({ to: `/employees/${employeeId}` });
  };

  const handleConfirm = async () => {
    if (!employee) {
      return;
    }

    try {
      if (employee.disabled) {
        await enableEmployeeMutation.mutateAsync({ id: employee._id });
        toast.success("Employee enabled", {
          description: (
            <>
              <span className="label">
                {employee.firstName} {employee.lastName}
              </span>{" "}
              has been enabled.
            </>
          ),
        });
      } else {
        await disableEmployeeMutation.mutateAsync({ id: employee._id });
        toast.success("Employee disabled", {
          description: (
            <>
              <span className="label">
                {employee.firstName} {employee.lastName}
              </span>{" "}
              has been disabled.
            </>
          ),
        });
      }
      await navigate({ to: "/employees" });
    } catch {
      toast.error(
        `Failed to ${employee.disabled ? "enable" : "disable"} employee`
      );
    }
  };

  if (isPending || !employee) {
    return (
      <ModalLoadingDialog
        message="Loading employee status..."
        onOpenChange={handleClose}
      />
    );
  }

  const isProcessing =
    disableEmployeeMutation.isPending || enableEmployeeMutation.isPending;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noScrollPrevention>
        <DialogTitle className="text-center">
          Are you sure you want to {employee.disabled ? "enable" : "disable"}{" "}
          this employee?
        </DialogTitle>
        <DialogDescription className="text-foreground text-center">
          {employee.disabled
            ? "Enabling an employee will allow them to log in to the POS."
            : "Disabling an employee will prevent them from logging in to the POS."}
        </DialogDescription>
        <DialogFooter>
          <Button
            className="w-full"
            onClick={handleClose}
            size="lg"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="w-full"
            disabled={isProcessing}
            onClick={handleConfirm}
            size="lg"
            type="button"
            variant="destructive"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
